import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  cleanObject,
  findPricingViaIdHelper,
  formatCurrency,
  formatDiscountInItemName,
  getSellingPriceWithoutDiscount,
  truncateString,
} from "../../../helpers";
import styles from "./ProductCarting.module.scss";
import primaryComponents from "../../primaryComponents";
import { motion } from "framer-motion";
import useProductCarting from "./useProductCarting";
import { DeleteBtnIcon, ProductCardEmptyIcon } from "../../../assets/svgIcons";
import useItemTracking from "../SellModeProductItems/ItemTrackingSelection/useItemTracking";
import ItemTrackingSelection from "../SellModeProductItems/ItemTrackingSelection";
import secondaryComponents from "..";
import { useStoreCurrency } from "../../../hooks/useStoreCurrency";
import { useMemo, useState } from "react";
import { RoutedModal } from "../../common";
import { ProductBatchSelection } from "../SellModeProductItems/ProductBatchSelection";
import { useSelector } from "react-redux";
import EditIconV2 from "../../../assets/svgIcons/EditIconV2";
import { NavLink, useParams } from "react-router-dom";
import { pathConstants } from "../../../routes/pathContants";

const ProductCarting = ({
  cartDetails,
  handleAddToCart,
  handleReduceCartItem,
  cartIndex,
  addCartItemToLocalStorage,
}) => {
  const { store_currency } = useStoreCurrency();

  const {
    quantity,
    cart_item_name,
    total_price,
    inputed_selling_price,
    product,
    service,
    discount_rate,
    custom_price,
    pricing_type_id,
    unit,
    vat,
    include_vat_in_price,
    item_numbers,
    unit_price,
  } = cartDetails;

  const {
    tagIsActive,
    customValue,
    pricingTypeId,
    discountType,
    discountValue,
    isLoadingAuthAction,
    triggerToast,
    storeProperties,
    setPricingTypeId,
    setCustomValue,
    setDiscountType,
    setDiscountValue,
    resetTagView,
    addCustomPricing,
    addLineDiscount,
    removeLineDiscount,
    pricings,
    changePricingType,
    handleOrderModuleAuth,
    updateUnit,
    checkMinimumCustomPricing,
  } = useProductCarting({
    custom_price,
    addCartItemToLocalStorage,
    product,
    pricing_type_id,
    cartIndex,
    manual_discount: cartDetails?.manual_discount,
  });

  const itemBasePrice = custom_price
    ? Number(custom_price)
    : Number(inputed_selling_price || unit_price || 0);

  const numDiscountVal = Number(discountValue) || 0;
  const computedDiscountPerUnit =
    discountType === "percentage"
      ? (numDiscountVal / 100) * itemBasePrice
      : numDiscountVal;

  const computedNewUnitPrice = Math.max(
    0,
    itemBasePrice - computedDiscountPerUnit,
  );
  const { productProperties, setProductProperties, closeModal } =
    useItemTracking();
  const isService = product?.is_service;
  const productTypeName = isService
    ? "store_service_properties"
    : "store_product_properties";
  const variant = product?.variant;
  const hasItemTracking = item_numbers && item_numbers?.length > 0;
  const productImage =
    product?.media_files?.[0]?.url ||
    product?.image_url ||
    service?.media_files?.[0]?.url ||
    service?.image_url;
  const stockUnits = useMemo(() => {
    if (!product) return [];

    const baseUnit = product.unit
      ? { unit: product.unit?.unit, id: product?.unit?.id, value: 1 }
      : null;
    const additionalUnits =
      product.additional_units?.map((additional_unit) => ({
        unit: additional_unit?.unit?.unit,
        id: additional_unit?.unit?.id,
        value: additional_unit?.value,
      })) || [];

    return baseUnit ? [baseUnit, ...additionalUnits] : additionalUnits;
  }, [product]);
  const [batchDetails, setBatchDetails] = useState(null);
  const { allow_custom_batch_name } = useSelector(
    (state) => state.profileDetailsReducer.businessDetails,
  );
  const { store_id } = useParams();
  return (
    <>
      <motion.div
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        className={styles.cartContainer}
      >
        <span className={styles.tagStatus}>
          {pricingTypeId === "custom"
            ? "Custom"
            : truncateString(
                findPricingViaIdHelper(pricingTypeId, pricings())?.pricing_type
                  ?.label,
                15,
              )}
        </span>

        <div className={styles["product"]}>
          <div className={styles["product-image"]}>
            {variant?.image_url || productImage ? (
              <img src={variant?.image_url || productImage} alt="" />
            ) : (
              <ProductCardEmptyIcon height="25" width="28" />
            )}
          </div>

          <div className={styles["product-details"]}>
            <p className={`${styles["product-name"]} dark--text`}>
              {truncateString(formatDiscountInItemName(cart_item_name), 50)}
            </p>
            <div className={styles["product-order"]}>
              <p className={"font-weight-semibold dark--text"}>
                <span className="mr-2 gray--text font-weight-normal">
                  {pricingTypeId === "custom"
                    ? "Custom"
                    : truncateString(
                        findPricingViaIdHelper(pricingTypeId, pricings())
                          ?.pricing_type?.label,
                        15,
                      )}
                  :
                </span>
                {formatCurrency({
                  value: total_price * Number(unit?.value),
                  currencyCode: store_currency,
                  decimal: 0,
                })}{" "}
                {custom_price ? (
                  <span className="ml-2 pending--rounded">C</span>
                ) : null}
                {cartDetails?.manual_discount ? (
                  <span className={"smallest-text-size ml-2"}>
                    <span
                      style={{
                        textDecoration: "line-through",
                        color: "#9091b3",
                      }}
                    >
                      {formatCurrency({
                        value:
                          itemBasePrice *
                          Number(quantity) *
                          Number(unit?.value || 1),
                        currencyCode: store_currency,
                        decimal: 0,
                      })}
                    </span>{" "}
                    <span className="error--text">
                      {cartDetails.manual_discount.type === "percentage"
                        ? `${Number(cartDetails.manual_discount.amount).toFixed(2)}% OFF`
                        : `-${formatCurrency({
                            value:
                              Number(cartDetails.manual_discount.amount) *
                              Number(quantity) *
                              Number(unit?.value || 1),
                            currencyCode: store_currency,
                            decimal: 0,
                          })}`}
                    </span>{" "}
                    <span
                      style={{
                        backgroundColor: "#E8F7EE",
                        color: "#19AE57",
                        borderRadius: "4px",
                        padding: "1px 6px",
                        fontSize: "11px",
                        fontWeight: 600,
                        display: "inline-block",
                        marginLeft: "4px",
                      }}
                    >
                      Item Discount
                    </span>
                  </span>
                ) : discount_rate > 0 &&
                  (custom_price === "" ||
                    custom_price === undefined ||
                    custom_price === null) ? (
                  <span className={"error--text smallest-text-size"}>
                    <span style={{ textDecoration: "line-through" }}>
                      {formatCurrency({
                        value: (
                          getSellingPriceWithoutDiscount({
                            vat_percent: vat,
                            inputed_selling_price,
                            discount_rate,
                            include_vat_in_price,
                          }) *
                          Number(quantity) *
                          Number(unit?.value)
                        ).toFixed(2),
                        currencyCode: store_currency,
                        decimal: 0,
                      })}
                    </span>{" "}
                    {discount_rate}% OFF
                  </span>
                ) : null}
              </p>
            </div>
          </div>
        </div>

        <div
          className="d-flex justify-content-between align-item-center mt-3"
          style={{ gap: "10px", flexWrap: "wrap" }}
        >
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <p
              className={styles.tagBtn}
              onClick={() => resetTagView("isPricing", !tagIsActive?.isPricing)}
            >
              <span>Change Pricing</span>{" "}
              <FontAwesomeIcon
                icon={`${tagIsActive?.isPricing ? "angle-up" : "angle-down"}`}
              />
            </p>

            <p
              className={styles.tagBtn}
              onClick={() =>
                resetTagView("isDiscount", !tagIsActive?.isDiscount)
              }
            >
              <span>
                {cartDetails?.manual_discount ? "Edit Discount" : "Discount"}
              </span>{" "}
              <FontAwesomeIcon
                icon={`${tagIsActive?.isDiscount ? "angle-up" : "angle-down"}`}
              />
            </p>
          </div>

          <div className={styles["product-actions"]}>
            {cartDetails?.bookings ? (
              <>
                <NavLink
                  to={pathConstants.SELL_MODE_SERVICE_DETAILS({
                    store_id,
                    service_id: product?.id,
                  })}
                >
                  <primaryComponents.Button
                    classNames={"btn btn--outline"}
                    style={{ height: "40px", fontSize: "14px" }}
                  >
                    Update Bookings <EditIconV2 className="ml-1" />
                  </primaryComponents.Button>
                </NavLink>
              </>
            ) : (
              <>
                <primaryComponents.Button
                  classNames={"btn btn--outline"}
                  style={{ height: "40px", width: "50px" }}
                  onClick={() => {
                    if (!hasItemTracking)
                      return handleReduceCartItem({
                        product,
                        quantity: Number(quantity) - 1,
                        variant: cartDetails?.product?.variant,
                      });
                    setProductProperties(
                      cleanObject({
                        product,
                        store_properties:
                          product?.[productTypeName]?.[0] ||
                          variant?.store_variant_properties?.[0],
                        variant,
                      }),
                    );
                  }}
                >
                  -
                </primaryComponents.Button>

                <primaryComponents.CartInputField
                  defaultValue={quantity}
                  updateCart={(val) => {
                    handleAddToCart({
                      product,
                      quantity: val,
                      variant: cartDetails?.product?.variant,
                    });
                  }}
                  disabled={hasItemTracking}
                  style={{ height: "40px", width: "50px" }}
                />
                <primaryComponents.Button
                  className={"btn btn--primary"}
                  onClick={() => {
                    if (!hasItemTracking)
                      return handleAddToCart({
                        product,
                        quantity: Number(quantity) + 1,
                        variant: cartDetails?.product?.variant,
                      });
                    setProductProperties(
                      cleanObject({
                        product,
                        store_properties:
                          product?.[productTypeName]?.[0] ||
                          variant?.store_variant_properties?.[0],
                        variant,
                      }),
                    );
                  }}
                  style={{ height: "40px", width: "50px" }}
                >
                  +
                </primaryComponents.Button>
              </>
            )}

            <primaryComponents.Button
              className={"btn btn--outline-red"}
              onClick={() =>
                handleReduceCartItem({
                  product,
                  quantity: 0,
                  variant: cartDetails?.product?.variant,
                })
              }
              style={{ height: "40px", width: "45px", padding: "0" }}
            >
              <DeleteBtnIcon />
            </primaryComponents.Button>
          </div>
        </div>

        <div className="d-flex mt-2">
          {allow_custom_batch_name && product?.has_custom_batch && (
            <primaryComponents.Button
              classNames={"btn btn--outline smallBtn mr-auto"}
              onClick={() =>
                setBatchDetails({
                  productId: product?.id,
                  variantId: variant?.id,
                })
              }
            >
              {cartDetails?.batch_id ? (
                <span className="d-flex align-items-center">
                  {truncateString(cartDetails?.batch_name, 12)}
                  <EditIconV2
                    width={15}
                    height={15}
                    className={"font-weight-normal ml-1"}
                    color="#000"
                  />
                </span>
              ) : (
                "Select batch"
              )}
            </primaryComponents.Button>
          )}

          {stockUnits?.length > 0 && (
            <primaryComponents.Button
              classNames={"btn btn--outline smallBtn ml-auto"}
              onClick={() => resetTagView("isUnit", !tagIsActive?.isUnit)}
            >
              <span className="mr-2">{unit?.unit}</span>{" "}
              <FontAwesomeIcon
                icon={`${tagIsActive?.isUnit ? "angle-up" : "angle-down"}`}
              />
            </primaryComponents.Button>
          )}
        </div>

        {tagIsActive?.isPricing && (
          <div className={styles.tagSection}>
            <h6>Select pricing for this item</h6>
            <div className="d-flex flex-column mt-2" style={{ gap: "10px" }}>
              {pricings()?.map((pricingItem, pricingIndex) => (
                <label
                  key={pricingIndex}
                  htmlFor={`pricingType${cartIndex}${pricingIndex}`}
                  className="d-flex align-items-center"
                  style={{ gap: "7px" }}
                >
                  <primaryComponents.RadioButton
                    name={`pricingType${cartIndex}`}
                    id={`pricingType${cartIndex}${pricingIndex}`}
                    value={pricingItem?.pricing_type?.id}
                    checked={
                      `${pricingTypeId}` === `${pricingItem?.pricing_type?.id}`
                    }
                    setRadioValue={(val) => {
                      setPricingTypeId(val);
                      changePricingType(cartIndex, val);
                    }}
                  />
                  <span>{pricingItem?.pricing_type?.label}</span>
                </label>
              ))}

              {storeProperties?.allow_custom_price && (
                <label
                  key={cartIndex}
                  htmlFor={`pricingTypeCustom${cartIndex}`}
                  className="d-flex align-items-center"
                  style={{ gap: "7px" }}
                >
                  <primaryComponents.RadioButton
                    name={`pricingType${cartIndex}`}
                    id={`pricingTypeCustom${cartIndex}`}
                    value={"custom"}
                    checked={pricingTypeId === "custom"}
                    setRadioValue={(val) => {
                      setPricingTypeId(val);
                    }}
                  />
                  <span>Custom</span>
                </label>
              )}
            </div>

            {storeProperties?.allow_custom_price &&
            pricingTypeId === "custom" ? (
              <div className={`${styles.tagSection}`}>
                <p className="font-weight-normal mb-2">
                  Enter custom price per unit
                </p>
                <primaryComponents.InputField
                  placeholder="NGN 0.00"
                  value={customValue}
                  type="number"
                  step={0.01}
                  onChange={({ target: { value } }) => {
                    setCustomValue(value);
                  }}
                />
                <div className={styles.btnContainer}>
                  <primaryComponents.Button
                    classNames="btn btn--primary smallBtn ml-auto"
                    onClick={() => {
                      if (customValue === "")
                        return triggerToast(
                          "Please input a value for custom pricing",
                          "warning",
                        );

                      if (!storeProperties?.allow_custom_price)
                        return triggerToast(
                          "Activate this feature in your store settings or contact your store admin for assistance",
                          "warning",
                        );
                      const { minimumCustomPrice } =
                        checkMinimumCustomPricing(unit_price);
                      if (
                        minimumCustomPrice > 0 &&
                        Number(customValue) < Number(minimumCustomPrice)
                      )
                        return triggerToast(
                          "The value is below the minimum custom value set, contact your store admin for assistance",
                          "warning",
                        );
                      handleOrderModuleAuth({
                        actionKey: "use_custom_pricing_on_order",
                        onSuccessCallBack: () => {
                          addCustomPricing(cartIndex);
                        },
                      });
                    }}
                    isLoading={isLoadingAuthAction}
                    disabled={isLoadingAuthAction}
                    loadingText={"Confirm..."}
                  >
                    Confirm
                  </primaryComponents.Button>
                </div>
              </div>
            ) : null}
          </div>
        )}

        {tagIsActive?.isDiscount && (
          <div className={styles.tagSection}>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6 className="mb-0">
                {cartDetails?.manual_discount
                  ? "Update Item Discount"
                  : "Apply Item Discount"}
              </h6>
              {cartDetails?.manual_discount && (
                <primaryComponents.Button
                  classNames="btn btn--outline-red smallBtn"
                  style={{
                    height: "28px",
                    fontSize: "12px",
                    padding: "0 10px",
                  }}
                  onClick={() => removeLineDiscount(cartIndex)}
                >
                  Remove Discount
                </primaryComponents.Button>
              )}
            </div>

            <div className="d-flex my-2" style={{ gap: "10px" }}>
              <primaryComponents.BoxedRadioInput
                name={`discountType${cartIndex}`}
                id={`discountTypeFlat${cartIndex}`}
                isChecked={discountType === "flat"}
                setRadioValue={() => setDiscountType("flat")}
              >
                Flat Amount ({store_currency})
              </primaryComponents.BoxedRadioInput>
              <primaryComponents.BoxedRadioInput
                name={`discountType${cartIndex}`}
                id={`discountTypePercent${cartIndex}`}
                isChecked={discountType === "percentage"}
                setRadioValue={() => setDiscountType("percentage")}
              >
                Percentage (%)
              </primaryComponents.BoxedRadioInput>
            </div>

            <p className="font-weight-normal mb-1 small-text-size">
              {discountType === "flat"
                ? `Enter discount per unit (${store_currency})`
                : "Enter discount percentage (%)"}
            </p>
            <primaryComponents.InputField
              placeholder={
                discountType === "flat" ? `${store_currency} 0.00` : "0%"
              }
              value={discountValue}
              type="number"
              min="0"
              max={discountType === "percentage" ? "100" : undefined}
              step={0.01}
              onChange={({ target: { value } }) => setDiscountValue(value)}
            />

            <div
              className="mt-2 p-2 rounded"
              style={{
                background: "#f8f9fa",
                border: "1px solid #eee",
                fontSize: "12px",
              }}
            >
              <div className="d-flex justify-content-between">
                <span className="gray--text">Base Price:</span>
                <span>
                  {formatCurrency({
                    value: itemBasePrice,
                    currencyCode: store_currency,
                  })}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="gray--text">Discount per unit:</span>
                <span className="text-danger">
                  -
                  {formatCurrency({
                    value: computedDiscountPerUnit,
                    currencyCode: store_currency,
                  })}
                  {discountType === "percentage" && discountValue
                    ? ` (${Number(discountValue).toFixed(2)}%)`
                    : ""}
                </span>
              </div>
              <div className="d-flex justify-content-between font-weight-bold">
                <span>New Unit Price:</span>
                <span className="text-success">
                  {formatCurrency({
                    value: computedNewUnitPrice,
                    currencyCode: store_currency,
                  })}
                </span>
              </div>
              <div className="d-flex justify-content-between">
                <span className="gray--text">
                  Total line discount ({quantity}x):
                </span>
                <span className="text-danger font-weight-semibold">
                  -
                  {formatCurrency({
                    value:
                      computedDiscountPerUnit *
                      Number(quantity) *
                      Number(unit?.value || 1),
                    currencyCode: store_currency,
                  })}
                </span>
              </div>
            </div>

            <div className={styles.btnContainer + " mt-3"}>
              <primaryComponents.Button
                classNames="btn btn--primary smallBtn ml-auto"
                onClick={() => addLineDiscount(cartIndex)}
                isLoading={isLoadingAuthAction}
                disabled={isLoadingAuthAction}
                loadingText={"Applying..."}
              >
                {cartDetails?.manual_discount
                  ? "Update Discount"
                  : "Apply Discount"}
              </primaryComponents.Button>
            </div>
          </div>
        )}

        {tagIsActive?.isUnit && (
          <div className={styles.tagSection}>
            <h6>Select unit for this item</h6>
            <div className="d-flex flex-column mt-2" style={{ gap: "10px" }}>
              {stockUnits?.map((stockUnit, position) => (
                <label
                  key={stockUnit?.id}
                  htmlFor={`unit${cartIndex}${stockUnit?.id}`}
                  className="d-flex align-items-center"
                  style={{ gap: "7px" }}
                >
                  <primaryComponents.RadioButton
                    name={`unit${cartIndex}`}
                    id={`unit${cartIndex}${stockUnit?.id}`}
                    value={stockUnit?.id}
                    checked={`${unit?.id}` === `${stockUnit?.id}`}
                    setRadioValue={(id) => {
                      const equivalentValue = stockUnits?.find(
                        (stockUnit) => `${stockUnit?.id}` === `${id}`,
                      )?.value;
                      updateUnit(
                        cartIndex,
                        id,
                        equivalentValue,
                        stockUnit?.unit,
                      );
                    }}
                  />
                  <span>{stockUnit?.unit}</span>
                </label>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      <secondaryComponents.Modal
        isActive={productProperties !== null}
        width={550}
        closeModal={closeModal}
      >
        <ItemTrackingSelection
          closeModal={closeModal}
          productProperties={productProperties}
          cartDetails={cartDetails}
          handleAddToCart={handleAddToCart}
          handleReduceCartItem={handleReduceCartItem}
        />
      </secondaryComponents.Modal>

      <RoutedModal
        isActive={batchDetails !== null}
        closeModal={() => setBatchDetails(null)}
        width={550}
        style={{ maxHeight: "auto", height: "auto", minHeight: "400px" }}
      >
        <ProductBatchSelection
          {...batchDetails}
          closeModal={() => setBatchDetails(null)}
        />
      </RoutedModal>
    </>
  );
};

export default ProductCarting;
